import React, { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { exportAlertsToExcel, exportAlertsToPdf } from '../utils/alertExport';

function ExportAlertsButton({ records = [], filterLabel = '', disabled = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(null);
  const ref = useRef(null);
  const count = records.length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runExport = async (type) => {
    if (!count || exporting) return;
    setExporting(type);
    try {
      const meta = { filterLabel, grouped: true };
      if (type === 'pdf') exportAlertsToPdf(records, meta);
      else exportAlertsToExcel(records, meta);
      setOpen(false);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled || count === 0}
        className="h-8 inline-flex items-center gap-1.5 px-3 text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md hover:bg-[#f6f7fa] transition disabled:opacity-40 disabled:cursor-not-allowed"
        title={count === 0 ? 'No alerts to export' : `Export ${count} alerts`}
      >
        {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        <span className="hidden sm:inline">Export</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50/80">
            <p className="text-xs font-semibold text-slate-800">Export for memo / client report</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{count} filtered alert{count !== 1 ? 's' : ''}</p>
          </div>
          <div className="py-1">
            <button
              type="button"
              onClick={() => runExport('pdf')}
              disabled={!!exporting}
              className="w-full text-left px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition disabled:opacity-50"
            >
              <FileText size={15} className="text-[#bd2f2b] flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">PDF report</p>
                <p className="text-[11px] text-slate-400">Print dialog → Save as PDF</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => runExport('excel')}
              disabled={!!exporting}
              className="w-full text-left px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition disabled:opacity-50"
            >
              <FileSpreadsheet size={15} className="text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">Excel (.csv)</p>
                <p className="text-[11px] text-slate-400">Spreadsheet with full detail</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExportAlertsButton;